import React, { useState, useRef, useEffect, useCallback } from 'react';
import styled from 'astroturf/react';
import { Button } from 'js/design_system/Button';
import { TextArea } from 'js/design_system/forms/TextArea';
import { Icon } from 'js/design_system/Icon';
import { Spinner } from 'js/design_system/Spinner';
import { Body } from 'js/design_system/Body';
import Markdown from 'markdown-to-jsx';

// Types
export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  isError?: boolean;
}

export interface ChatInterfaceProps {
  messages: ChatMessage[];
  onSendMessage: (message: string) => void | Promise<void>;
  isLoading?: boolean;
  placeholder?: string;
  showTypingIndicator?: boolean;
  error?: string | null;
  onClearError?: () => void;
  onClearChat?: () => void;
  className?: string;
  title?: string;
}

// Styled Components
const ChatContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  background: var(--background-white, #ffffff);
  border: 1px solid var(--stroke-light, #e5e7eb);
  border-radius: var(--border-radius-radius-md);
  overflow: hidden;
`;

const ChatHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--spacing-xs-12, 12px) var(--spacing-xs-16, 16px);
  background: var(--background-beige-light);
  border-bottom: 1px solid var(--stroke-light, #e5e7eb);
  flex-shrink: 0;
`;

const ChatTitle = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-xs-8, 8px);
  color: var(--text-primary, #111827);
  font-weight: var(--type-body-sm-medium-font-weight, 500);
`;

const MessagesContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: var(--spacing-xs-16, 16px);
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs-12, 12px);
  background: var(--background-white, #ffffff);

  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: var(--stroke-light, #e5e7eb);
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: var(--stroke-medium, #9ca3af);
  }
`;

const MessageWrapper = styled('div')<{
  variant: 'user' | 'assistant' | 'system';
}>`
  display: flex;

  &.variant-user {
    justify-content: flex-end;
  }

  &.variant-assistant,
  &.variant-system {
    justify-content: flex-start;
  }
`;

const MessageCard = styled('div')<{
  variant: 'user' | 'assistant' | 'system' | 'error';
}>`
  max-width: 85%;
  background: var(--background-white, #ffffff);
  border: 1px solid var(--stroke-light, #e5e7eb);
  border-radius: var(--border-radius-radius-lg);
  padding: var(--spacing-xs-12, 12px) var(--spacing-xs-16, 16px);
  box-shadow:
    0 1px 3px 0 rgba(0, 0, 0, 0.05),
    0 1px 2px 0 rgba(0, 0, 0, 0.03);
  word-wrap: break-word;

  &.variant-user {
    background: var(--background-light-blue, #f0f6ff);
    border-color: var(--brand-colors-blue-300, #c7d7fc);
  }

  &.variant-assistant {
    background: var(--background-white, #ffffff);
  }

  &.variant-system {
    background: var(--accent-yellow, #fef3c7);
    border-color: var(--premium-gold);
  }

  &.variant-error {
    background: var(--brand-colors-error-100, #fee2e2);
    border-color: var(--brand-colors-error-500, #fca5a5);
  }
`;

const MessageLabel = styled.div`
  font-size: var(--type-body-xs-size, 12px);
  font-weight: var(--type-body-xs-font-weight, 500);
  color: var(--text-secondary, #6b7280);
  margin-bottom: var(--spacing-xs-4, 4px);
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const MessageContent = styled.div`
  font-size: var(--type-body-sm-size, 14px);
  line-height: var(--type-body-sm-line-height, 20px);
  white-space: pre-wrap;
  color: var(--text-primary, #111827);
`;

const TypingIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-xs-4, 4px);
`;

const TypingDot = styled.span`
  width: 8px;
  height: 8px;
  background: var(--text-secondary, #6b7280);
  border-radius: 50%;
  opacity: 0.6;
  animation: typing-dot 1.4s infinite;

  &:nth-child(2) {
    animation-delay: 0.2s;
  }

  &:nth-child(3) {
    animation-delay: 0.4s;
  }

  @keyframes typing-dot {
    0%,
    60%,
    100% {
      opacity: 0.6;
    }
    30% {
      opacity: 1;
    }
  }
`;

const InputSection = styled.div`
  padding: var(--spacing-xs-16, 16px);
  background: var(--background-white, #ffffff);
  border-top: 1px solid var(--stroke-light, #e5e7eb);
  flex-shrink: 0;
`;

const InputContainer = styled.div`
  display: flex;
  gap: var(--spacing-xs-8, 8px);
  align-items: stretch;
`;

const TextAreaWrapper = styled.div`
  flex: 1;

  textarea {
    min-height: 44px;
    max-height: 120px;
    resize: vertical;
  }
`;

const SendButton = styled(Button)`
  flex-shrink: 0;
  align-self: stretch;
  height: auto;
  margin: 8px 0;
`;

const ErrorMessage = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-xs-8, 8px);
  padding: var(--spacing-xs-8, 8px) var(--spacing-xs-12, 12px);
  background: var(--brand-colors-error-100, #fee2e2);
  color: var(--brand-colors-error-500, #dc2626);
  border-radius: var(--border-radius-radius-md);
  font-size: var(--type-body-sm-size, 14px);
  margin-bottom: var(--spacing-xs-12, 12px);
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: var(--spacing-md-32, 32px) var(--spacing-xs-16, 16px);
  text-align: center;
  color: var(--text-secondary, #6b7280);
  flex: 1;
`;

export const ChatInterface: React.FC<ChatInterfaceProps> = ({
  messages,
  onSendMessage,
  isLoading = false,
  placeholder = 'Type your message...',
  showTypingIndicator = true,
  error,
  onClearError,
  onClearChat,
  className,
  title = 'AI Assistant',
}) => {
  const [inputValue, setInputValue] = useState('');
  const [isSending, setIsSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-scroll to bottom when new messages arrive
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Handle sending messages
  const handleSend = async () => {
    const trimmedMessage = inputValue.trim();
    if (!trimmedMessage || isSending || isLoading) return;

    setInputValue('');
    setIsSending(true);

    try {
      await onSendMessage(trimmedMessage);
    } catch (error) {
      console.error('Failed to send message:', error);
    } finally {
      setIsSending(false);
      // Refocus the input after sending
      setTimeout(() => {
        textAreaRef.current?.focus();
      }, 100);
    }
  };

  // Handle Enter key press
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const showLoadingIndicator = isLoading || isSending;

  return (
    <ChatContainer className={className}>
      <ChatHeader>
        <ChatTitle>
          <Icon name="chat" size={20} />
          <Body size="sm" weight="medium">
            {title}
          </Body>
        </ChatTitle>
        {onClearChat && messages.length > 0 && (
          <Button
            size="xs"
            onClick={onClearChat}
            icon={<Icon name="refresh" size={16} />}
            title="Clear chat"
          />
        )}
      </ChatHeader>

      <MessagesContainer>
        {messages.length === 0 && (
          <EmptyState>
            <Icon name="chat_bubble" size={48} style={{ opacity: 0.3 }} />
            <Body size="sm" style={{ marginTop: '16px' }}>
              Start a conversation by typing a message below
            </Body>
          </EmptyState>
        )}

        {messages.map((message) => (
          <MessageWrapper key={message.id} variant={message.role}>
            <MessageCard variant={message.isError ? 'error' : message.role}>
              <MessageLabel>
                {message.role === 'user' ? 'You' : 'AI Assistant'}
              </MessageLabel>
              <MessageContent>
                {message.role === 'user' ? (
                  message.content
                ) : (
                  <Markdown>{message.content}</Markdown>
                )}
              </MessageContent>
            </MessageCard>
          </MessageWrapper>
        ))}

        {showLoadingIndicator && showTypingIndicator && (
          <MessageWrapper variant="assistant">
            <MessageCard variant="assistant">
              <MessageLabel>AI Assistant</MessageLabel>
              <TypingIndicator>
                <TypingDot />
                <TypingDot />
                <TypingDot />
              </TypingIndicator>
            </MessageCard>
          </MessageWrapper>
        )}

        <div ref={messagesEndRef} />
      </MessagesContainer>

      <InputSection>
        {error && (
          <ErrorMessage>
            <Icon name="error_outline" size={16} />
            <span>{error}</span>
            {onClearError && (
              <Icon
                name="close"
                size={16}
                style={{ marginLeft: 'auto', cursor: 'pointer' }}
                onClick={onClearError}
              />
            )}
          </ErrorMessage>
        )}

        <InputContainer>
          <TextAreaWrapper>
            <TextArea
              ref={textAreaRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              disabled={showLoadingIndicator}
              aria-label="Message input"
            />
          </TextAreaWrapper>
          <SendButton
            size="sm"
            onClick={handleSend}
            disabled={!inputValue.trim() || showLoadingIndicator}
            icon={
              showLoadingIndicator ? (
                <Spinner size={16} />
              ) : (
                <Icon name="send" size={16} />
              )
            }
            aria-label="Send message"
          />
        </InputContainer>
      </InputSection>
    </ChatContainer>
  );
};

export default ChatInterface;
